import { useState, useEffect } from "react";
import { Link, Navigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowRight, Sparkles, Shield, Heart, BookOpen, Eye, MessageSquare, Calendar,
  Compass, History, Layers, BrainCircuit, Lock, Mic,
} from "lucide-react";
import { api } from "@/lib/api";
import { SeoHead } from "@/components/SeoHead";
import { HeroAnimation } from "@/components/HeroAnimation";
import { ProductGallery } from "@/components/ProductGallery";

export function Home() {
  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <SeoHead
        title="Private relationship guidance | SugarSense"
        description="Think through mixed signals, different expectations and what to say next. Private relationship guidance that takes in the whole story. Start free."
        path="/"
      />

      {/* ─── HERO ─── */}
      <section className="ss-bg-radiant border-b border-champagne relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden="true">
          <svg viewBox="0 0 1200 600" className="absolute top-0 right-0 w-[60%] -translate-y-12 translate-x-16" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="ghero" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#A66A72" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#A66A72" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="900" cy="300" r="350" fill="url(#ghero)" />
          </svg>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-24 relative grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-[13px] sm:text-sm tracking-[0.4em] uppercase text-mauve mb-6">Private relationship guidance</p>
            <h1 className="font-heading text-4xl sm:text-6xl md:text-[4.25rem] text-cocoa leading-[1.05]">
              Make sense of what <span className="italic brand-shine">happened</span>.
              <br />Work out what to do next.
            </h1>
            <p className="mt-7 max-w-xl text-cocoa/70 text-base sm:text-lg leading-relaxed">
              You've replayed it a dozen times. Tell SugarSense the whole story, your part included,
              and get a clearer take on what's going on, the right words when it's time to talk,
              and a way to see whether things actually change.
            </p>
            <p className="mt-4 max-w-xl text-cocoa/60 text-sm sm:text-base leading-relaxed" data-testid="hero-audience-line">
              For mixed signals, crossed wires and the knotty, many-sided situations that come up in relationships of every kind.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register"><button data-testid="home-cta-primary" className="ss-pill ss-pill-primary h-12 w-full sm:w-auto px-7">
                Try it free <ArrowRight size={16} />
              </button></Link>
              <Link to="/pricing"><button data-testid="home-cta-plans" className="ss-pill ss-pill-ghost h-12 w-full sm:w-auto">See plans</button></Link>
            </div>
            <p className="mt-4 text-sm text-cocoa/55" data-testid="hero-entitlement-line">Start with one situation. Ongoing history is available on paid plans.</p>
            <p className="mt-5 text-xs tracking-widest uppercase text-cocoa/40" data-testid="hero-strapline">
              Use a nickname · Start free · <Link to="/security" className="underline underline-offset-2 hover:text-cocoa">Privacy explained</Link>
            </p>
          </div>
          <div className="relative min-w-0">
            <HeroAnimation />
          </div>
        </div>
      </section>

      <ProductGallery />

      {/* ─── THREE PILLARS — what this actually is ─── */}
      <section className="border-b border-champagne bg-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve text-center mb-4">What you get</p>
          <h2 className="font-heading text-3xl sm:text-5xl text-cocoa text-center leading-tight max-w-3xl mx-auto">
            Advice you can use.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BrainCircuit,
                t: "Consider another explanation",
                d: "Look at what happened, what might be missing and whether your own response played a part. You can question a first impression without dismissing your feelings.",
              },
              {
                icon: History,
                t: "Find the words",
                d: "Get help saying what you mean, setting a boundary or asking a question. Adapt the words until they feel like yours.",
              },
              {
                icon: Compass,
                t: "Keep the longer view",
                d: "Keep conversations, promises and updates together, separately for each person. Look back when something new makes the history relevant.",
                note: "Ongoing history is available on paid plans.",
              },
            ].map((f, i) => (
              <div key={i} className="ss-card-premium p-7">
                <f.icon className="text-mauve mb-4" size={22} strokeWidth={1.5} />
                <h3 className="font-heading text-2xl text-cocoa">{f.t}</h3>
                <p className="mt-3 text-cocoa/70 text-[15px] leading-relaxed">{f.d}</p>
                {f.note && <p className="mt-3 text-xs text-cocoa/50" data-testid="benefit-history-note">{f.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS — continuity, not one-shot ─── */}
      <section className="border-b border-champagne bg-blush/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid md:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-4">How it works</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-cocoa leading-tight">Start with one situation.</h2>
            <p className="mt-4 text-cocoa/70 max-w-md">
              Your first situation is free. Add what happens next when you're ready, and on paid
              plans SugarSense keeps that history in mind for later advice.
            </p>
          </div>
          <ol className="space-y-5">
            {[
              {
                t: "Add the person",
                d: "Use a nickname for the person you're thinking about.",
              },
              {
                t: "Describe what happened",
                d: "Include what they said or did, your own response and what you'd like help with. Type it, or on Luxe say it out loud and we'll transcribe it.",
              },
              {
                t: "Choose what to do next",
                d: "Consider the advice, ask for help with a reply and decide what fits your situation.",
              },
            ].map((s, i) => (
              <li key={i} className="ss-card p-6 flex gap-5">
                <span className="font-heading text-3xl text-mauve shrink-0">{i + 1}</span>
                <div>
                  <p className="font-heading text-lg text-cocoa">{s.t}</p>
                  <p className="mt-1 text-cocoa/70 text-[15px] leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── CONTINUITY — how it evolves with you ─── */}
      <section className="border-b border-champagne bg-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
            <div>
              <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-4">Over time</p>
              <h2 className="font-heading text-3xl sm:text-5xl text-cocoa leading-tight">
                See what <span className="italic brand-shine">changes</span> over time.
              </h2>
              <p className="mt-6 text-cocoa/75 leading-relaxed">
                A promise matters differently after it has been kept, changed or broken. Add what
                happened next, and SugarSense can use that update alongside the relevant history
                you've shared.
              </p>
              <p className="mt-5 text-cocoa/75 leading-relaxed">
                All of this stays inside your account. Names and identifying details are stripped
                out before your words go anywhere, and you can delete the lot whenever you choose.
              </p>
              <p className="mt-4 text-sm text-cocoa/55" data-testid="continuity-entitlement-line">Ongoing history is available on paid plans.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register"><button className="ss-pill ss-pill-primary h-11 px-6">Start free <ArrowRight size={14} /></button></Link>
                <Link to="/about"><button className="ss-pill ss-pill-ghost h-11">How it works</button></Link>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { icon: History, t: "Keep each person separate", d: "Keep notes about each person together, so one relationship's history doesn't become another's." },
                { icon: Layers,  t: "Add the missing context",   d: "A correction or explanation can change how an earlier event should be read." },
                { icon: Eye, t: "Notice follow-through", d: "Record what happened after a conversation, including signs of progress and problems that remain." },
              ].map((f, i) => (
                <div key={i} className="ss-card p-5 flex gap-4">
                  <f.icon className="text-mauve shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <div>
                    <p className="font-heading text-lg text-cocoa">{f.t}</p>
                    <p className="mt-1 text-cocoa/70 text-[14px] leading-relaxed">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── BUILT FOR YOU — discretion + ethics ─── */}
      <section className="bg-blush/30 border-b border-champagne">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve text-center mb-3">Built for you</p>
          <h3 className="font-heading text-3xl sm:text-5xl text-cocoa text-center leading-tight max-w-3xl mx-auto">When the relationship is hard to explain.</h3>
          <p className="mt-5 text-cocoa/70 text-center max-w-2xl mx-auto leading-relaxed">
            You might be dating again, in an age-gap relationship or dealing with different expectations.
            SugarSense gives you a private place to think it through, whatever your gender or theirs.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { icon: Heart,  t: "Good together. Uncertain between dates.", d: "Think through the gap between how things feel in person and what happens afterwards." },
              { icon: Compass, t: "Dating again", d: "Take time to work out what you want and how you'd like to handle things this time." },
              { icon: Shield, t: "When expectations don't match", d: "Talk through gifts, favours, time and effort, and what either of you is hoping for from the relationship." },
            ].map((t, i) => (
              <div key={i} className="ss-card-premium p-6">
                <t.icon className="text-mauve mb-3" size={20} strokeWidth={1.5} />
                <h4 className="font-heading text-xl text-cocoa">{t.t}</h4>
                <p className="mt-2 text-cocoa/70 text-[15px] leading-relaxed">{t.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INTELLIGENCE ENGINE — quiet IP positioning ─── */}
      <section className="bg-ivory border-b border-champagne">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-4">Our approach</p>
          <h3 className="font-heading text-3xl sm:text-5xl text-cocoa leading-tight max-w-3xl mx-auto">
            What the advice draws on.
          </h3>
          <p className="mt-6 text-cocoa/70 max-w-2xl mx-auto leading-relaxed">
            SugarSense brings together a structured library of relationship patterns, the situation
            you describe and the relevant history you've shared. It offers possible explanations and
            practical options. You decide what fits.
          </p>
          <p className="mt-5 text-cocoa/55 text-sm max-w-2xl mx-auto leading-relaxed" data-testid="research-entitlement-line">
            Ongoing history is available on paid plans.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/research"><button data-testid="home-link-research" className="ss-pill ss-pill-ghost h-11">Our approach</button></Link>
            <Link to="/archetypes"><button data-testid="home-link-archetypes" className="ss-pill ss-pill-ghost h-11">Browse relationship patterns</button></Link>
            <Link to="/security"><button data-testid="home-link-privacy" className="ss-pill ss-pill-ghost h-11">Privacy explained</button></Link>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-ivory">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h3 className="font-heading text-3xl sm:text-5xl text-cocoa leading-tight">
            Start with what happened.
          </h3>
          <p className="mt-4 text-cocoa/70 max-w-xl mx-auto">
            A message, a changed plan or a conversation you keep replaying. Bring one situation
            and see whether another perspective helps.
          </p>
          <Link to="/register"><button data-testid="home-footer-cta" className="ss-pill ss-pill-gold h-12 mt-8 px-8">Try it free <ArrowRight size={16} /></button></Link>
          <p className="mt-5 text-sm text-cocoa/55" data-testid="footer-cta-entitlement">
            Start free. Paid plans add more situations, ongoing history and reports. <Link to="/pricing" className="underline underline-offset-2 hover:text-cocoa">See plans</Link>
          </p>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

// Shared footer — used by Home AND all StaticPages so the bottom navigation
// always renders on /about, /faq, /security, /contact, /privacy, /terms.
export function SiteFooter() {
  return (
    <div className="border-t border-champagne bg-ivory">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" data-testid="footer-logo" className="block text-center font-heading text-[1.75rem] md:text-3xl text-cocoa tracking-tight mb-5">
          Sugar<span className="italic font-medium text-mauve">Sense</span>
        </Link>
        <Link
          to="/about#evolves"
          data-testid="footer-built-by"
          className="block text-center text-[11px] tracking-[0.35em] uppercase text-mauve/80 hover:text-mauve transition-colors"
        >
          Built on careful research · shaped by the members who use it →
        </Link>
        <div className="mt-6 pt-6 border-t border-champagne/60 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-cocoa/50">
          <span>© 2026 SugarSense</span>
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center">
            <Link to="/about"    data-testid="footer-about"    className="hover:text-cocoa">About</Link>
            <Link to="/pricing"  data-testid="footer-pricing"  className="hover:text-cocoa">Pricing</Link>
            <Link to="/faq"      data-testid="footer-faq"      className="hover:text-cocoa">FAQ</Link>
            <Link to="/security" data-testid="footer-security" className="hover:text-cocoa">Security</Link>
            <Link to="/contact"  data-testid="footer-contact"  className="hover:text-cocoa">Contact</Link>
            <Link to="/privacy"  data-testid="footer-privacy"  className="hover:text-cocoa">Privacy</Link>
            <Link to="/terms"    data-testid="footer-terms"    className="hover:text-cocoa">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StaticPage({ title, lead, children, testid }) {
  return (
    <>
      <div data-testid={testid} className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <h1 className="font-heading text-4xl sm:text-5xl text-cocoa">{title}</h1>
        {lead && <p className="mt-4 text-cocoa/70 text-lg leading-relaxed">{lead}</p>}
        <div className="prose mt-8 text-cocoa/80 space-y-5 leading-relaxed">{children}</div>
      </div>
      <SiteFooter />
    </>
  );
}

const SKU_FAMILY = { plus: "sense_plus", luxe: "luxe" }; // tiers purchasable today via Square
const TIER_CTA = { free: "Start free", lite: "Request Lite", plus: "Choose Plus", premium: "Request Premium", luxe: "Go Luxe" };
const money = (v) => (v == null ? "—" : v === 0 ? "$0" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

function planFeatures(p) {
  const f = [];
  if (p.ai_insights_per_month != null) f.push(`${p.ai_insights_per_month} situation${p.ai_insights_per_month === 1 ? "" : "s"} a month`);
  if (p.max_profiles != null) f.push(p.max_profiles >= 9999 ? "Unlimited relationship profiles" : `${p.max_profiles} relationship profile${p.max_profiles === 1 ? "" : "s"}`);
  if (p.max_reports_per_month) f.push(`${p.max_reports_per_month} pattern report${p.max_reports_per_month === 1 ? "" : "s"}/month`);
  if (p.max_drafts_per_month) f.push(`${p.max_drafts_per_month} message drafts/month`);
  f.push(p.deep_memory_enabled ? "Ongoing history across your reads" : "One situation at a time (no ongoing history)");
  if (p.voice_notes_enabled) f.push("Voice notes — speak it, we transcribe it");
  if (p.priority_processing) f.push("Priority AI processing");
  if (p.key !== "free") f.push("Discreet mode & private history");
  return f;
}

export function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    api.get("/plans").then((r) => setPlans((r.data?.plans || r.data || []).filter((p) => p.active !== false)))
      .catch(() => setPlans([]));
  }, []);
  const featuredKey = plans?.some((p) => p.featured) ? plans.find((p) => p.featured).key : "plus";
  const TIERS = (plans || []).map((p) => {
    const fam = SKU_FAMILY[p.key];
    const sku = fam ? (a) => `${fam}_${a ? "annual" : "monthly"}` : null;
    return {
      key: p.key, name: p.name, tagline: p.tagline, testid: `tier-${p.key.replace("_", "-")}`, featured: p.key === featuredKey,
      priceMonthly: p.key === "free" ? "$0 USD" : `${money(p.price_monthly_usd)} USD/mo`,
      priceAnnual: p.key === "free" ? "$0 USD" : `${money(p.price_annual_usd)} USD/yr`,
      features: planFeatures(p), sku, voice: !!p.voice_notes_enabled,
      cta: TIER_CTA[p.key] || `Choose ${p.name}`,
      to: p.key === "free" ? "/register" : sku ? (a) => `/billing?plan=${sku(a)}` : `/contact?subject=${encodeURIComponent(`${p.name} plan access`)}`,
    };
  });
  const seo = plans && plans.length ? plans.filter((p) => p.key !== "free").map((p) => `${p.name} (${money(p.price_monthly_usd)}/mo or ${money(p.price_annual_usd)}/yr)`).join(", ") : "flexible plans";
  return (
    <div data-testid="pricing-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <SeoHead
        title="Pricing — SugarSense"
        description={`Free, ${seo}. Private AI relationship strategist for affluent and emotionally complex dating. Cancel anytime.`}
        path="/pricing"
      />
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-4">Pricing</p>
      <h1 className="font-heading text-4xl sm:text-6xl text-cocoa">Simple plans. Real privacy.</h1>
      <p className="mt-4 text-cocoa/70 max-w-xl" data-testid="pricing-intro">Start free. Paid plans add more reads, ongoing history and reports. See the allowances below. Prices in USD. Cancel anytime.</p>

      <div className="mt-8 inline-flex items-center bg-champagne rounded-full p-1" data-testid="pricing-cycle-toggle">
        <button onClick={() => setAnnual(false)} data-testid="cycle-monthly"
          className={`px-5 h-9 rounded-full text-sm transition-colors ${!annual ? "bg-cocoa text-ivory" : "text-cocoa/70"}`}>
          Monthly
        </button>
        <button onClick={() => setAnnual(true)} data-testid="cycle-annual"
          className={`px-5 h-9 rounded-full text-sm transition-colors ${annual ? "bg-cocoa text-ivory" : "text-cocoa/70"}`}>
          Annual <span className="ml-1 text-mauve text-xs">−20%</span>
        </button>
      </div>

      {plans === null && <p className="mt-10 text-sm text-cocoa/50" data-testid="pricing-loading">Loading plans…</p>}
      <div className={`mt-10 grid gap-5 ${TIERS.length >= 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3"}`} data-testid="pricing-tiers">
        {TIERS.map((t) => {
          const price = annual ? t.priceAnnual : t.priceMonthly;
          const to = typeof t.to === "function" ? t.to(annual) : t.to;
          return (
            <div key={t.name} data-testid={t.testid} className={`ss-card p-7 flex flex-col ${t.featured ? "ring-2 ring-mauve scale-[1.02]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading text-2xl text-cocoa">{t.name}</h3>
                {t.voice && <span data-testid={`${t.testid}-voice-badge`} className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/50 rounded-full px-2 py-0.5"><Mic size={9} /> New · Voice</span>}
              </div>
              {t.tagline && <p className="mt-1 text-xs text-cocoa/60 min-h-[2rem]" data-testid={`${t.testid}-tagline`}>{t.tagline}</p>}
              <div className="mt-3 font-heading text-4xl text-cocoa" data-testid={`${t.testid}-price`}>{price}</div>
              {annual && t.key !== "free" && <p className="text-xs text-mauve mt-1">Save vs paying monthly</p>}
              <ul className="mt-6 space-y-2 text-sm text-cocoa/80 flex-1">
                {t.features.map((f, i) => <li key={i} className="flex gap-2"><span className="text-mauve">·</span>{f}</li>)}
              </ul>
              <Link to={to} className="mt-7"><button className={`w-full ss-pill ${t.featured ? "ss-pill-primary" : "ss-pill-ghost"} h-11`}>{t.cta}</button></Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function About() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);
  return (
    <StaticPage testid="about-page"
      title="A second opinion you can actually use."
      lead="SugarSense is a private place to think through what's going on in a relationship. Tell it what happened, your part included, and get a clearer take on it, the right words for the conversation you've been putting off, and a way to see what changes over time.">
      <p>
        Most relationship advice is written for people whose situation fits a tidy story.
        Plenty of us don't. The person who texts every day and never makes a plan. The
        apology that arrives as flowers instead of words. The arrangement that looks like
        one thing from the outside and feels like another from the inside. SugarSense was
        made for the messy middle, whatever your gender or theirs.
      </p>
      <h2 className="font-heading text-2xl text-cocoa mt-8">What makes it different</h2>
      <p>
        It isn't a search engine and it doesn't improvise. Behind every answer is a library
        of relationship patterns we've researched and written up in depth, so when you describe
        what's happening, SugarSense can say which patterns fit, which don't, and what people
        in your position have found useful. When the picture is unclear, it says so and gives
        you the alternatives rather than a confident guess.
      </p>
      <p>
        It also pays attention to your side of things. Asking for space isn't a mistake, and
        an explanation doesn't erase a broken plan. The aim is a fuller picture, not a verdict
        on anyone's character.
      </p>
      <h2 id="evolves" className="font-heading text-2xl text-cocoa mt-8">How it gets better</h2>
      <p>
        The pattern library and the advice are reviewed and refined all the time, with
        structured feedback from members who use the service. On paid plans, SugarSense also
        keeps the history you've shared about each person, so a promise made in March still
        counts in June and an update you add later can change how an earlier event should be read.
      </p>
      <p>
        We don't introduce anyone. We don't read your partner's messages. We don't compare you
        to other members. We work only from what <em>you</em> describe.
      </p>
      <h2 className="font-heading text-2xl text-cocoa mt-8">Private by design</h2>
      <p>
        Free to begin, yours to delete at any moment, encrypted end-to-end.
      </p>
    </StaticPage>
  );
}

export function Faq() {
  const QS = [
    [
      "Is this a dating site?",
      "No. SugarSense doesn't introduce anyone, rank anyone or run a matching service. It's a private place to think through a relationship you already have, or one you're trying to make sense of.",
    ],
    [
      "Is this just a chatbot?",
      "No. A general chatbot answers from whatever it has read on the internet. SugarSense works from its own library of relationship patterns, the situation you describe and, on paid plans, the history you've shared. It tells you which patterns fit, offers the words for a conversation, and keeps track of what happens next.",
    ],
    [
      "What does it actually do?",
      "Three things. It helps you make sense of what happened, including the parts that might be missing. It helps you find the words, whether that's a reply, a boundary or a question. And on paid plans it keeps the longer view, so you can see whether things are really changing or just repeating.",
    ],
    [
      "How sure is it?",
      "SugarSense never claims to know what another person is thinking. It tells you which patterns best fit what you've described, says when the picture is unclear, and gives you the alternatives. You decide what fits.",
    ],
    [
      "What happens to what I write?",
      "Your words stay in your account. Before anything is used to prepare your advice, names, contact details, places, amounts and other identifying details are removed or replaced with neutral placeholders. Nobody on our team can read your entries without a recorded reason.",
    ],
    [
      "Can I delete everything?",
      "Yes. One click deletes every entry, person and note tied to your account. There's no waiting period and nothing kept back. You can also download everything you've written at any time.",
    ],
    [
      "Who is it for?",
      "Anyone trying to make sense of a relationship that's hard to explain, whatever your gender or theirs. That might mean dating again after a long time, an age-gap relationship, or expectations that don't quite match. SugarSense keeps its language neutral until you tell it how you and the people in your life identify.",
    ],
    [
      "Will my partner ever know I'm using this?",
      "Only if you tell them. SugarSense sends no messages to anyone else and leaves no public trace. Discreet Mode hides what's on screen when you need a moment of privacy.",
    ],
    [
      "What if something I describe is actually unsafe?",
      "If what you describe suggests threats, control or abuse, SugarSense puts a calm safety note with confidential helpline numbers for your country ahead of any other advice. We never contact anyone on your behalf. What you do next is your call, but you'll see the support first.",
    ],
  ];
  return (
    <StaticPage testid="faq-page" title="Frequently asked.">
      <div className="space-y-5 mt-2">
        {QS.map(([q, a]) => (
          <div key={q} className="ss-card p-6">
            <div className="font-heading text-xl text-cocoa">{q}</div>
            <p className="mt-2 text-cocoa/80 leading-relaxed">{a}</p>
          </div>
        ))}
      </div>
    </StaticPage>
  );
}

export function Security() {
  return (
    <StaticPage testid="security-page"
      title="Your story stays yours."
      lead="The short version of how we look after what you share, and why no one, not even us, can read your entries without a recorded reason.">

      <h2 className="font-heading text-2xl text-cocoa mt-6">How we hold ourselves to account</h2>
      <p>
        We build and test SugarSense against recognised security and privacy standards,
        including ISO/IEC 27001, the OWASP guidance for web applications, and the GDPR and
        CCPA rules on your data rights. We treat these as the floor, not the ceiling.
      </p>

      <h2 className="font-heading text-2xl text-cocoa mt-8">Kept safe</h2>
      <p>
        Everything you write is scrambled so it can't be read if it's intercepted or
        stolen, both while it travels to us and while it's stored. Backups are protected
        the same way. Your words are never written into our technical logs.
      </p>

      <h2 className="font-heading text-2xl text-cocoa mt-8">What happens to what you write</h2>
      <p>
        Before your words are used to prepare your advice, names, contact details,
        addresses, workplaces, places, amounts and other identifying details are
        removed or replaced with neutral placeholders. The outside services that help
        write your advice only ever see that version. The original stays in your
        account, where they can't reach it.
      </p>
      <p>
        We don't sell your data. We don't share it with advertisers or data brokers.
        We don't use what you write to train anyone else's systems. Our analytics are
        opt-in and never include what you've written about your relationships.
      </p>

      <h2 className="font-heading text-2xl text-cocoa mt-8">Who can read your entries</h2>
      <p>
        You. Nobody on our team can open an individual account's entries without a
        recorded administrative action and a documented reason, and we're clear about
        when that's allowed: suspected abuse of the service, a legal requirement, or an
        account recovery you've asked for. Every such access is logged for a year.
      </p>

      <h2 className="font-heading text-2xl text-cocoa mt-8">Your rights</h2>
      <p>
        One click downloads everything you've ever written. One click deletes everything,
        with no waiting period and nothing kept back. You don't need to email us, wait, or
        give a reason.
      </p>

      <h2 className="font-heading text-2xl text-cocoa mt-8">Want more detail?</h2>
      <p>
        We're happy to share our fuller security documentation under a confidentiality
        agreement. Reach out via our <Link to="/contact" className="text-mauve underline">contact form</Link>.
      </p>
    </StaticPage>
  );
}

export function Safety() {
  // Retained for legacy /safety routes — redirects to /security.
  return <Navigate to="/security" replace />;
}

export function Contact() {
  const DRAFT_KEY = "ss.contact.draft";
  const [form, setForm] = useState(() => {
    try { return { name: "", email: "", subject: "", message: "", ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") }; }
    catch { return { name: "", email: "", subject: "", message: "" }; }
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [confirmShort, setConfirmShort] = useState(false);
  // Functional updates (never spread a stale closure — autofill fires several fields at once)
  const setField = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };
  useEffect(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* private mode */ } }, [form]);
  // Enter in a single-line field must never submit the form (it used to send half-typed messages).
  const blockEnter = (e) => { if (e.key === "Enter") e.preventDefault(); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 40 && !confirmShort) { setConfirmShort(true); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/contact", { ...form, subject: form.subject.trim(), message: form.message.trim() });
      setSent(true);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <StaticPage testid="contact-page"
      title="Talk to us."
      lead="We read every message that comes through this form. We don't reply from this page directly — once we've read your note, a member of the SugarSense team will follow up by email if a reply is needed.">
      {sent ? (
        <div data-testid="contact-sent" className="ss-card-premium p-8 bg-sage/15">
          <h2 className="font-heading text-2xl text-cocoa">Thank you.</h2>
          <p className="mt-2 text-cocoa/80">Your message has reached us. If a reply is needed, we'll come back to you at <strong>{form.email}</strong>.</p>
        </div>
      ) : (
        <form data-testid="contact-form" onSubmit={submit} className="ss-card p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Your name</span>
              <input data-testid="contact-name" required value={form.name} onKeyDown={blockEnter}
                onChange={setField("name")}
                maxLength={80}
                className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Email</span>
              <input data-testid="contact-email" required type="email" value={form.email} onKeyDown={blockEnter}
                onChange={setField("email")}
                className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Subject (optional)</span>
            <input data-testid="contact-subject" value={form.subject} onKeyDown={blockEnter}
              onChange={setField("subject")}
              maxLength={160}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Message</span>
            <textarea data-testid="contact-message" required rows={6} value={form.message}
              onChange={setField("message")}
              maxLength={4000} minLength={10}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md p-3" />
          </label>
          {err && <p data-testid="contact-error" className="text-rose text-sm">{err}</p>}
          {confirmShort && (
            <p data-testid="contact-confirm-short" className="text-sm text-cocoa/75 bg-gold/15 border border-gold/40 rounded-md p-3">
              Your message is quite short ({form.message.trim().length} characters). If that's everything you meant to say, press <strong>Send message</strong> again — otherwise keep writing.
            </p>
          )}
          <p className="text-xs text-cocoa/50" data-testid="contact-draft-note">Your draft is saved on this device until it's sent. {form.message.length}/4000</p>
          <button data-testid="contact-submit" disabled={busy} type="submit"
            className="ss-pill ss-pill-primary h-11 px-6 disabled:opacity-50">
            {busy ? "Sending…" : confirmShort ? "Yes, send as is" : "Send message"}
          </button>
        </form>
      )}
    </StaticPage>
  );
}


// ─── Essays browse ────────────────────────────────────────────────────────
const ESSAY_CAT_LABEL = {
  patterns:              "Patterns",
  provider_psychology:   "Provider psychology",
  mixed_signals:         "Mixed signals",
  emotional_intelligence:"Emotional intelligence",
  archetype_explainer:   "Archetype explainer",
  lifestyle:             "Lifestyle",
};

export function Essays() {
  const [essays, setEssays] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/essays").then((r) => setEssays(r.data || [])); }, []);
  const cats = Array.from(new Set(essays.map((e) => e.category)));
  let visible = cat === "all" ? essays : essays.filter((e) => e.category === cat);
  if (q.trim()) {
    const needle = q.trim().toLowerCase();
    visible = visible.filter((e) => (e.title || "").toLowerCase().includes(needle) || (e.subtitle || "").toLowerCase().includes(needle));
  }
  const featured = visible[0];
  const rest = visible.slice(1);
  return (
    <div data-testid="essays-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
      <SeoHead
        title="Editorial — Notes on the dynamic"
        description="Short, considered essays on affluent, provider-style and emotionally complex dating — the patterns, the psychology, the elegant moves."
        path="/essays"
      />
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-3">Editorial</p>
      <h1 className="font-heading text-4xl sm:text-6xl text-cocoa leading-tight">Notes on the dynamic.</h1>
      <p className="mt-4 text-cocoa/70 max-w-2xl text-base sm:text-lg leading-relaxed">
        Short, considered essays on affluent, provider-style and emotionally complex dating —
        the patterns, the psychology, the elegant moves.
      </p>

      {/* Search */}
      <div className="mt-7 max-w-md">
        <input
          data-testid="essays-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search essays…" type="search"
          className="w-full bg-ivory border border-champagne rounded-full h-11 px-5 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none"
        />
      </div>

      <div className="mt-6 flex gap-2 flex-wrap" data-testid="essays-cat-filter">
        <button onClick={() => setCat("all")} className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest ${cat === "all" ? "bg-cocoa text-ivory" : "bg-champagne/60 text-cocoa/70"}`}>All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`essays-cat-${c}`}
            className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest ${cat === c ? "bg-cocoa text-ivory" : "bg-champagne/60 text-cocoa/70"}`}>
            {ESSAY_CAT_LABEL[c] || c}
          </button>
        ))}
      </div>

      {/* Featured essay */}
      {featured && cat === "all" && !q && (
        <Link to={`/essays/${featured.slug}`} data-testid="essays-featured" className="mt-10 block ss-card-premium p-7 sm:p-10 group">
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Featured · {ESSAY_CAT_LABEL[featured.category] || featured.category}</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-cocoa group-hover:text-mauve transition-colors leading-tight">{featured.title}</h2>
          {featured.subtitle && <p className="mt-4 font-heading italic text-lg sm:text-xl text-cocoa/70 max-w-2xl">{featured.subtitle}</p>}
          <div className="mt-6 flex items-center gap-3 text-[11px] tracking-widest uppercase text-cocoa/40">
            <span>{featured.reading_time_min || 3} min read</span>
            <span>·</span>
            <span className="text-mauve">Read essay →</span>
          </div>
        </Link>
      )}

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {visible.length === 0 && <p className="text-cocoa/50 text-sm">No essays match.</p>}
        {(cat === "all" && !q ? rest : visible).map((e) => (
          <Link to={`/essays/${e.slug}`} key={e.slug} data-testid={`essay-card-${e.slug}`} className="ss-card-premium p-6 sm:p-7 group block">
            <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{ESSAY_CAT_LABEL[e.category] || e.category}</p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl text-cocoa group-hover:text-mauve transition-colors leading-tight">{e.title}</h2>
            {e.subtitle && <p className="mt-3 text-cocoa/70 leading-relaxed">{e.subtitle}</p>}
            <p className="mt-5 text-[11px] tracking-widest uppercase text-cocoa/40">{e.reading_time_min || 3} min read</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function EssayDetail() {
  const { slug } = useParams();
  const [essay, setEssay] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    setEssay(null); setRelated([]);
    api.get(`/essays/${slug}`).then((r) => {
      setEssay(r.data);
      // Same-category related essays
      api.get(`/essays?category=${encodeURIComponent(r.data.category)}&limit=8`)
        .then((rr) => setRelated((rr.data || []).filter((e) => e.slug !== r.data.slug).slice(0, 3)))
        .catch(() => {});
    }).catch((e) => setErr(e?.response?.data?.detail || "Essay not found"));
  }, [slug]);
  if (err) return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-cocoa/60"><p>{err}</p><Link to="/essays" className="mt-6 inline-block text-mauve underline">← Back to editorial</Link></div>;
  if (!essay) return <div className="max-w-2xl mx-auto px-4 py-24 text-cocoa/40">Loading…</div>;
  const apiBase = (
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    process.env.REACT_APP_BACKEND_URL || ""
  ).replace(/\/$/, "");
  const ogImg = `${apiBase}/api/og/essay/${essay.slug}`;
  const wordCount = (essay.body_md || "").split(/\s+/).length;
  const pullQuote = extractPullQuote(essay.body_md);
  return (
    <article data-testid="essay-detail" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
      <SeoHead
        title={essay.title}
        description={essay.subtitle || essay.title}
        path={`/essays/${essay.slug}`}
        type="article"
        image={ogImg}
        article={{
          publishedAt: essay.published_at,
          updatedAt: essay.updated_at,
          category: essay.category,
          readingMin: essay.reading_time_min,
          wordCount,
          author: essay.author || "SugarSense Editorial",
        }}
      />
      <Link to="/essays" className="text-xs tracking-widest uppercase text-mauve">← Editorial</Link>
      <p className="mt-6 text-[11px] tracking-[0.4em] uppercase text-mauve">{ESSAY_CAT_LABEL[essay.category] || essay.category} · {essay.reading_time_min || 3} min read</p>
      <h1 className="mt-4 font-heading text-4xl sm:text-5xl text-cocoa leading-tight">{essay.title}</h1>
      {essay.subtitle && <p className="mt-5 font-heading italic text-xl text-cocoa/70">{essay.subtitle}</p>}
      <div className="mt-10 ss-article" dangerouslySetInnerHTML={{ __html: renderMarkdown(essay.body_md) }} />

      {/* Paywall — non-auth visitors see preview then sign-up wall */}
      {essay.is_preview && (
        <aside data-testid="essay-paywall" className="mt-8 ss-card-premium p-8 sm:p-10 text-center bg-blush/20 relative">
          <div className="absolute -top-12 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-ivory pointer-events-none" />
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Read on, privately</p>
          <h3 className="mt-3 font-heading text-2xl sm:text-3xl text-cocoa max-w-md mx-auto leading-tight">
            The rest of this essay — and the full library — for members.
          </h3>
          <p className="mt-4 text-cocoa/70 max-w-lg mx-auto leading-relaxed text-sm sm:text-base">
            Free to start. Every essay is editorially considered, fact-checked, and written in calm,
            grown-up language. No moralising. No clinical jargon. Just the patterns and what to do.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="essay-paywall-register">
              <button className="ss-pill ss-pill-primary h-12 px-7 w-full sm:w-auto">Sign up free <ArrowRight size={16} /></button>
            </Link>
            <Link to="/login"><button className="ss-pill ss-pill-ghost h-12 w-full sm:w-auto">I have an account</button></Link>
          </div>
          <p className="mt-5 text-xs tracking-widest uppercase text-cocoa/40">{essay.full_word_count?.toLocaleString() || ""} words · {essay.reading_time_min || 3} min read in full</p>
        </aside>
      )}

      {/* — Conversion rail — pull-quote + Try-the-AI CTA — (logged-in users) */}
      {!essay.is_preview && (
      <aside data-testid="essay-cta-rail" className="mt-16 ss-card-premium p-7 sm:p-10 text-center">
        {pullQuote && (
          <p className="font-heading italic text-2xl sm:text-3xl text-cocoa/85 leading-snug mx-auto max-w-xl">
            &ldquo;{pullQuote}&rdquo;
          </p>
        )}
        <div className={`${pullQuote ? "mt-8 pt-8 border-t border-champagne/70" : ""}`}>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Reading the pattern is the first step</p>
          <h3 className="mt-3 font-heading text-2xl sm:text-3xl text-cocoa max-w-md mx-auto leading-tight">
            Run your situation through your own private strategist.
          </h3>
          <p className="mt-4 text-cocoa/70 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
            Describe what's happening in your own words. SugarSense's AI reads the dynamic,
            names the archetype, drafts your next move — and quietly remembers the pattern
            so it gets sharper over time.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="essay-cta-register">
              <button className="ss-pill ss-pill-primary h-12 px-7 w-full sm:w-auto">
                Try SugarSense free <ArrowRight size={16} />
              </button>
            </Link>
            <Link to="/pricing" data-testid="essay-cta-pricing">
              <button className="ss-pill ss-pill-ghost h-12 w-full sm:w-auto">See plans</button>
            </Link>
          </div>
          <p className="mt-5 text-xs tracking-widest uppercase text-cocoa/40">3 free insights · No card required · Yours to delete</p>
        </div>
      </aside>
      )}

      {/* — Related essays — */}
      {related.length > 0 && (
        <section data-testid="essay-related" className="mt-16">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-5">More on {ESSAY_CAT_LABEL[essay.category] || essay.category}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link to={`/essays/${r.slug}`} key={r.slug} data-testid={`essay-related-${r.slug}`}
                className="ss-card p-5 group block hover:shadow-md transition-all">
                <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{r.reading_time_min || 3} min</p>
                <p className="mt-3 font-heading text-xl text-cocoa group-hover:text-mauve transition-colors leading-tight line-clamp-3">{r.title}</p>
                {r.subtitle && <p className="mt-2 text-sm text-cocoa/65 leading-relaxed line-clamp-2">{r.subtitle}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-14 pt-10 border-t border-champagne flex items-center justify-between">
        <p className="text-xs tracking-widest uppercase text-cocoa/40">SugarSense Editorial</p>
        <Link to="/essays" className="text-sm text-mauve">All essays →</Link>
      </div>
    </article>
  );
}

/** Pull a tasteful italic standalone sentence (10–180 chars) from the essay body
 * for the conversion-rail pull-quote. Falls back to null if none qualifies. */
function extractPullQuote(md) {
  if (!md) return null;
  const candidates = [];
  // Italic standalone lines (e.g. the closing takeaway essays end with).
  const italicLines = md.match(/^\s*\*([^*\n]{20,200})\*\s*$/gm);
  if (italicLines) {
    italicLines.forEach((l) => {
      const clean = l.replace(/^\s*\*|\*\s*$/g, "").trim();
      candidates.push(clean);
    });
  }
  // Prefer the LAST italic line (usually the takeaway).
  if (candidates.length) return candidates[candidates.length - 1];
  // Fallback: first short, punchy sentence (≤ 180 chars, not in a heading or list).
  const lines = md.split("\n").filter((l) => l && !l.startsWith("#") && !l.startsWith("-") && !l.startsWith(">"));
  for (const l of lines) {
    const m = l.match(/[^.!?]{20,180}[.!?]/);
    if (m) return m[0].trim();
  }
  return null;
}

// Minimal safe markdown renderer (headings, bold, italic, lists, blockquote, paragraphs).
// Avoids react-markdown dependency, escapes raw HTML.
function renderMarkdown(md) {
  if (!md) return "";
  // 1. Escape HTML
  let s = md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // 2. Headings (drop the first # title — page already shows it).
  // Use \n boundary so single-line bodies don't get wiped entirely.
  s = s.replace(/^# [^\n]*\n?/, "");
  s = s.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  s = s.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  // 3. Horizontal rule
  s = s.replace(/^---$/gm, "<hr/>");
  // 4. Blockquote
  s = s.replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");
  // 5. Inline emphasis
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[\s(])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  // 6. Unordered lists (group consecutive lines starting with - )
  s = s.replace(/(^- .+(\n- .+)*)/gm, (m) => {
    const items = m.split("\n").map((l) => `<li>${l.replace(/^- /, "")}</li>`).join("");
    return `<ul>${items}</ul>`;
  });
  // 7. Paragraphs (any remaining non-tag block)
  s = s.split(/\n{2,}/).map((p) => {
    const t = p.trim();
    if (!t) return "";
    if (t.startsWith("<")) return t;
    return `<p>${t.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return s;
}
